/**
 * Signer-roster editor (per ADR-0044).
 *
 * Lives inside the pre-send signing panel. A *party* is the legal entity
 * (a company, or a person); *signers* are the humans who actually sign.
 * A person party pre-seeds one signer (themselves); a company party gets
 * its firmatecknare / VD / ombud added here. The roster is saved via
 * `signing.set_signers` and drives both the rendered underskrift block
 * and the Scrive request at send time.
 *
 * The editor manages a local draft and persists on "Spara signerare".
 * Emails / personnummer are not required to save (validated at send),
 * so a partial roster can be parked mid-edit.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Copy, UserCircle2 } from 'lucide-react';
import {
  tenantApi,
  type GetSignersResponse,
  type SignerAuthMethod,
  type SignerHidePersonalNumber,
  type SignerRosterEntry,
  type SignerRosterParty,
  type SignerType,
} from '../api.js';
import { useTenantQuery } from '../query.js';

type DraftSigner = {
  key: string;
  partyId: string;
  signerType: SignerType | null;
  legalName: string;
  email: string;
  phone: string;
  personalNumber: string;
  authMethod: SignerAuthMethod;
  hidePersonalNumber: SignerHidePersonalNumber;
  showPersonalNumberInBlock: boolean;
};

/** The `set_signers` payload shape (one row per signer, server assigns id). */
export type RosterPayload = Array<Omit<SignerRosterEntry, 'id'>>;

/**
 * Map the local draft to the persisted payload, assigning contiguous
 * signer ordinals per party in display order. Single source of truth for
 * both the explicit save and the publish-to-parent path so what you see
 * is exactly what gets sent.
 */
function draftToPayload(draft: DraftSigner[]): RosterPayload {
  const perParty = new Map<string, number>();
  return draft.map((s) => {
    const ord = perParty.get(s.partyId) ?? 0;
    perParty.set(s.partyId, ord + 1);
    return {
      party_id: s.partyId,
      signer_ordinal: ord,
      signer_type: s.signerType,
      legal_name: s.legalName.trim() || '—',
      email: s.email.trim() || null,
      phone: s.phone.trim() || null,
      personal_number: s.personalNumber.trim() || null,
      auth_method: s.authMethod,
      hide_personal_number: s.hidePersonalNumber,
      show_personal_number_in_block: s.showPersonalNumberInBlock,
    };
  });
}

const SIGNER_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: '— (ingen angiven)' },
  { value: 'firmatecknare', label: 'Firmatecknare' },
  { value: 'vd', label: 'VD' },
  { value: 'ombud', label: 'Ombud' },
  { value: 'other', label: 'Annat' },
];

const HIDE_PN_OPTIONS: ReadonlyArray<{ value: SignerHidePersonalNumber; label: string }> = [
  { value: 'none', label: 'Visa på verifieringssidan' },
  { value: 'verification', label: 'Dölj på verifieringssidan' },
];

let keySeq = 0;
const nextKey = () => `s${++keySeq}`;

function toDraft(s: GetSignersResponse['signers'][number]): DraftSigner {
  return {
    key: nextKey(),
    partyId: s.party_id,
    signerType: s.signer_type,
    legalName: s.legal_name,
    email: s.email ?? '',
    phone: s.phone ?? '',
    personalNumber: s.personal_number ?? '',
    authMethod: s.auth_method,
    hidePersonalNumber: s.hide_personal_number,
    showPersonalNumberInBlock: s.show_personal_number_in_block,
  };
}

/** Role-based hide-PN default (per ADR-0044): lessor/employer hide from
 *  the verification page; the incoming lessee shows. */
function defaultHidePn(role: SignerRosterParty['role']): SignerHidePersonalNumber {
  return role === 'lessor' ? 'verification' : 'none';
}

function blankSigner(
  party: SignerRosterParty,
  recommendPnInBlock: boolean,
): DraftSigner {
  return {
    key: nextKey(),
    partyId: party.party_id,
    signerType: party.id_type === 'org_no' ? 'firmatecknare' : null,
    legalName: '',
    email: '',
    phone: '',
    personalNumber: '',
    authMethod: 'se_bankid',
    hidePersonalNumber: defaultHidePn(party.role),
    showPersonalNumberInBlock: recommendPnInBlock,
  };
}

export function SignerRosterEditor({
  contractId,
  method = 'scrive',
  onSaved,
  onRosterChange,
  parent = null,
}: {
  contractId: string;
  /**
   * Which signing workflow this roster feeds. For `manual` the email /
   * BankID / personnummer fields are hidden — manual signing only needs
   * the names for the underskrift block.
   */
  method?: 'scrive' | 'manual';
  /** Called after a successful save so the parent can refresh readiness. */
  onSaved?: () => void | Promise<void>;
  /**
   * Publishes the current roster payload on every edit so the parent
   * (Send button) can persist exactly what's on screen before sending —
   * closing the gap where unsaved edits were silently dropped at send.
   */
  onRosterChange?: (payload: RosterPayload) => void;
  /**
   * When this contract is a connected child, its parent. Surfaces a
   * "Kopiera signerare från <parent>" action that pulls the parent's
   * roster and remaps it onto this contract's parties by (role, ordinal)
   * — so a besittningsskydd-överenskommelse inherits its lease's signers
   * without re-typing them. The user reviews + saves afterwards.
   */
  parent?: { id: string; title: string } | null;
}) {
  const rosterQ = useTenantQuery(
    'signing.get_signers',
    (cid) => tenantApi.signing.get_signers({ contract_id: cid as string }),
    [contractId],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );

  // Used by "Signera som mig själv" on the lessor — pre-fills the
  // signed-in user's name (e.g. the landlord rep) into a fresh signer
  // row. Personnummer is left blank by design: BankID is opt-in and the
  // user decides whether to fill it later.
  const { user: clerkUser } = useUser();
  const selfName = clerkUser?.fullName?.trim() || null;
  const selfEmail = clerkUser?.primaryEmailAddress?.emailAddress ?? null;

  const parties = useMemo(() => rosterQ.data?.parties ?? [], [rosterQ.data]);
  const recommendPnInBlock = rosterQ.data?.recommend_pn_in_block ?? false;
  const [draft, setDraft] = useState<DraftSigner[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  // Pull the parent contract's roster and remap it onto this child's
  // parties by (role, ordinal). A parent signer whose role/ordinal has no
  // counterpart here (e.g. a guarantor that the överenskommelse doesn't
  // carry) is skipped; child-only slots (e.g. make/sambo) stay empty for
  // the user to fill. Replaces the current draft — this is a setup action,
  // run on a fresh child roster.
  const copyFromParent = useCallback(async () => {
    if (!parent) return;
    setCopyBusy(true);
    setCopyMsg(null);
    setErr(null);
    try {
      const src = await tenantApi.signing.get_signers({ contract_id: parent.id });
      const roleOrdByParty = new Map(
        src.parties.map((p) => [p.party_id, `${p.role}:${p.ordinal}`]),
      );
      const childByRoleOrd = new Map(
        parties.map((p) => [`${p.role}:${p.ordinal}`, p]),
      );
      const mapped: DraftSigner[] = [];
      let unmatched = 0;
      for (const s of src.signers) {
        const ro = roleOrdByParty.get(s.party_id);
        const childParty = ro ? childByRoleOrd.get(ro) : undefined;
        if (!childParty) {
          unmatched += 1;
          continue;
        }
        mapped.push({
          key: nextKey(),
          partyId: childParty.party_id,
          signerType: s.signer_type,
          // '—' is draftToPayload's empty-name fallback; treat it as blank
          // on the way back so the user is prompted to fill it.
          legalName: s.legal_name === '—' ? '' : s.legal_name,
          email: s.email ?? '',
          phone: s.phone ?? '',
          personalNumber: s.personal_number ?? '',
          authMethod: s.auth_method,
          hidePersonalNumber: s.hide_personal_number,
          showPersonalNumberInBlock: s.show_personal_number_in_block,
        });
      }
      if (mapped.length === 0) {
        setCopyMsg(
          'Inga signerare kunde mappas — parternas roller matchar inte det överordnade avtalet.',
        );
        return;
      }
      setDraft(mapped);
      setSaved(false);
      setCopyMsg(
        `Kopierade ${mapped.length} signerare från ${parent.title}.` +
          (unmatched > 0
            ? ` ${unmatched} kunde inte mappas och hoppades över.`
            : '') +
          ' Granska och spara.',
      );
    } catch (e) {
      setErr(
        (e as { message?: string }).message ??
          'Kunde inte hämta signerare från det överordnade avtalet',
      );
    } finally {
      setCopyBusy(false);
    }
  }, [parent, parties]);

  // Publish the current payload upward on every edit so Send persists
  // the on-screen roster, not a stale last-saved one.
  useEffect(() => {
    if (draft !== null) onRosterChange?.(draftToPayload(draft));
  }, [draft, onRosterChange]);

  // Seed the local draft once the roster loads.
  useEffect(() => {
    if (rosterQ.data && draft === null) {
      setDraft(rosterQ.data.signers.map(toDraft));
    }
  }, [rosterQ.data, draft]);

  const update = useCallback((key: string, patch: Partial<DraftSigner>) => {
    setSaved(false);
    setDraft((d) => d?.map((s) => (s.key === key ? { ...s, ...patch } : s)) ?? d);
  }, []);

  const addSigner = useCallback(
    (party: SignerRosterParty) => {
      setSaved(false);
      setDraft((d) => [...(d ?? []), blankSigner(party, recommendPnInBlock)]);
    },
    [recommendPnInBlock],
  );

  // "Signera som mig själv" — same shape as a blank signer but seeded
  // with the signed-in user's identity. PN stays blank (per the spec)
  // so the user opts into BankID consciously.
  const addSelfSigner = useCallback(
    (party: SignerRosterParty) => {
      setSaved(false);
      setDraft((d) => [
        ...(d ?? []),
        {
          ...blankSigner(party, recommendPnInBlock),
          legalName: selfName ?? '',
          email: selfEmail ?? '',
        },
      ]);
    },
    [selfName, selfEmail, recommendPnInBlock],
  );

  const removeSigner = useCallback((key: string) => {
    setSaved(false);
    setDraft((d) => d?.filter((s) => s.key !== key) ?? d);
  }, []);

  const save = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.signing.set_signers({
        contract_id: contractId,
        signers: draftToPayload(draft),
      });
      setSaved(true);
      await onSaved?.();
    } catch (e) {
      setErr((e as { message?: string }).message ?? 'Kunde inte spara signerare');
    } finally {
      setBusy(false);
    }
  }, [draft, contractId, onSaved]);

  if (rosterQ.isLoading || draft === null) {
    return <div className="text-xs text-stone-500">Laddar signerare…</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {parent && (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => void copyFromParent()}
            disabled={copyBusy}
            className="inline-flex items-center gap-1.5 self-start rounded border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50"
            title={`Hämta signerare från det överordnade avtalet (${parent.title}) och mappa dem på rollerna här`}
          >
            <Copy className="h-3 w-3" />
            {copyBusy ? 'Kopierar…' : `Kopiera signerare från ${parent.title}`}
          </button>
          {copyMsg && (
            <span className="text-[11px] text-stone-500">{copyMsg}</span>
          )}
        </div>
      )}
      {parties.map((party) => {
        const partySigners = draft.filter((s) => s.partyId === party.party_id);
        const isOrg = party.id_type === 'org_no';
        return (
          <div
            key={party.party_id}
            className="rounded-md border border-stone-200 bg-stone-50/40 p-2.5"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-baseline gap-2">
                <span className="truncate text-xs font-medium text-stone-800">
                  {party.label ?? party.legal_name}
                </span>
                {party.label && (
                  <span className="truncate text-[11px] text-stone-400">
                    {party.legal_name}
                  </span>
                )}
              </div>
              <span className="shrink-0 rounded-full border border-stone-200 bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-500">
                {isOrg ? 'Företag' : 'Person'}
              </span>
            </div>

            <ul className="mt-2 flex flex-col divide-y divide-stone-200/70">
              {partySigners.map((s, i) => (
                <SignerRow
                  key={s.key}
                  signer={s}
                  index={i}
                  method={method}
                  showType={isOrg}
                  recommendPnInBlock={recommendPnInBlock}
                  canRemove={partySigners.length > 1 || isOrg}
                  onChange={(patch) => update(s.key, patch)}
                  onRemove={() => removeSigner(s.key)}
                />
              ))}
            </ul>

            {/*
              An org party with no signer is a soft-error state — the
              send-time preflight will block until at least one human is
              named. Surface it calmly so the user knows what to do.
            */}
            {isOrg && partySigners.length === 0 && (
              <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                Ingen undertecknare vald. Ett företag måste ha en namngiven
                firmatecknare / VD / ombud.
              </div>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => addSigner(party)}
                className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
              >
                + Lägg till undertecknare
              </button>
              {party.role === 'lessor' && selfName && (
                <button
                  type="button"
                  onClick={() => addSelfSigner(party)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                  title={`Lägg till dig själv (${selfName}) som undertecknare`}
                >
                  <UserCircle2 className="h-3 w-3" />
                  Signera som mig själv
                </button>
              )}
            </div>
          </div>
        );
      })}

      {err && (
        <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
          {err}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy}
          className="self-start rounded border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
        >
          {busy ? 'Sparar…' : 'Spara signerare'}
        </button>
        {saved && !busy && (
          <span className="text-xs text-emerald-700">Sparat ✓</span>
        )}
      </div>
    </div>
  );
}

function SignerRow({
  signer,
  index,
  method,
  showType,
  recommendPnInBlock,
  canRemove,
  onChange,
  onRemove,
}: {
  signer: DraftSigner;
  index: number;
  method: 'scrive' | 'manual';
  showType: boolean;
  recommendPnInBlock: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<DraftSigner>) => void;
  onRemove: () => void;
}) {
  const usesBankId = signer.authMethod === 'se_bankid';
  const esign = method === 'scrive';
  // In manual delivery we hide the Scrive-only fields (BankID is irrelevant
  // out-of-band), but the personnummer input still has to appear when the
  // signer prints it in the document body.
  const showPnInput = (esign && usesBankId) || signer.showPersonalNumberInBlock;
  return (
    <li className="py-2.5 first:pt-1">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">
          Undertecknare {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[11px] text-stone-400 hover:text-rose-600"
          >
            Ta bort
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Namn" className="col-span-2">
          <input
            value={signer.legalName}
            onChange={(e) => onChange({ legalName: e.target.value })}
            className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
          />
        </Field>

        {showType && (
          <Field label="Roll">
            <select
              value={signer.signerType ?? ''}
              onChange={(e) =>
                onChange({ signerType: (e.target.value || null) as SignerType | null })
              }
              className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
            >
              {SIGNER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        )}

        {esign && (
          <Field label="E-post" className={showType ? '' : 'col-span-2'}>
            <input
              type="email"
              value={signer.email}
              onChange={(e) => onChange({ email: e.target.value })}
              className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
            />
          </Field>
        )}

        <Field label="Telefon" className={esign ? '' : 'col-span-2'}>
          <input
            value={signer.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
          />
        </Field>

        {esign && (
          <Field label="Autentisering">
            <select
              value={signer.authMethod}
              onChange={(e) =>
                onChange({ authMethod: e.target.value as SignerAuthMethod })
              }
              className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
            >
              <option value="se_bankid">BankID</option>
              <option value="standard">Utan BankID</option>
            </select>
          </Field>
        )}

        {/* Personnummer input — required for BankID (Scrive), and needed
            whenever the signer prints it in the document body, so it stays
            available in the manual workflow too. */}
        {showPnInput && (
          <Field
            label="Personnummer"
            className={`${esign && !usesBankId ? 'opacity-50' : ''}${esign ? '' : ' col-span-2'}`.trim()}
            hint={esign && usesBankId ? 'Krävs för BankID' : undefined}
          >
            <input
              value={signer.personalNumber}
              onChange={(e) => onChange({ personalNumber: e.target.value })}
              placeholder="ÅÅÅÅMMDD-XXXX"
              className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
            />
          </Field>
        )}

        {/* Print personnummer beneath the name in the signing block —
            independent of Scrive, applies in every signing mode. */}
        <Field
          label="Personnummer i avtalet"
          className="col-span-2"
          hint={recommendPnInBlock ? 'Rekommenderas för detta avtal' : undefined}
        >
          <select
            value={signer.showPersonalNumberInBlock ? 'yes' : 'no'}
            onChange={(e) =>
              onChange({ showPersonalNumberInBlock: e.target.value === 'yes' })
            }
            className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
          >
            <option value="no">Visa inte personnummer i avtalet</option>
            <option value="yes">Visa personnummer under namnet</option>
          </select>
        </Field>

        {/* Scrive verification-page visibility — Scrive only; the evidence
            log always retains the personnummer. */}
        {esign && (
          <Field label="Personnummer på verifieringssidan" className="col-span-2">
            <select
              value={signer.hidePersonalNumber}
              onChange={(e) =>
                onChange({
                  hidePersonalNumber: e.target.value as SignerHidePersonalNumber,
                })
              }
              className="w-full rounded border border-stone-300 px-2 py-1 text-xs"
            >
              {HIDE_PN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {signer.hidePersonalNumber === 'verification' && (
              <span className="mt-1 text-[10px] text-stone-500">
                Personnumret döljs endast på Scrives verifieringssida — det
                finns alltid kvar i den bifogade bevislogiken (evidence log).
              </span>
            )}
          </Field>
        )}
      </div>
    </li>
  );
}

function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-0.5 ${className ?? ''}`}>
      <span className="text-[10px] text-stone-500">
        {label}
        {hint ? <span className="ml-1 text-stone-400">· {hint}</span> : null}
      </span>
      {children}
    </label>
  );
}
